import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Skip auth check for superadmin pages to avoid unnecessary 401 errors
        if (window.location.pathname.startsWith('/superadmin')) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, []);

    const checkAuth = async () => {
        // Handle login-as token injected by superadmin
        const loginAsToken = localStorage.getItem('login_as_token');
        if (loginAsToken) {
            localStorage.setItem('token', loginAsToken);
            localStorage.removeItem('login_as_token');
            localStorage.removeItem('login_as_user');
        }
        try {
            const response = await authAPI.getMe();
            setUser(response.data);
        } catch (error) {
            setUser(null);
        }
        setLoading(false);
    };

    const login = async (email, password) => {
        const response = await authAPI.login({ email, password });
        const { user: userData, token } = response.data;
        if (token) {
            localStorage.setItem('token', token);
        }
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return userData;
    };

    const register = async (name, email, password, invitationToken = null, companyInvitation = null) => {
        const response = await authAPI.register({ name, email, password }, invitationToken, companyInvitation);
        const { user: userData, token } = response.data;
        if (token) {
            localStorage.setItem('token', token);
        }
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return userData;
    };

    // Google OAuth login
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const loginWithGoogle = async (credential) => {
        const response = await authAPI.googleAuth({ credential });
        const { user: userData, token } = response.data;
        if (token) {
            localStorage.setItem('token', token);
        }
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        return userData;
    };

    const logout = async () => {
        try {
            await authAPI.logout();
        } catch (e) {
            // ignore
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        window.location.href = '/login';
    };

    const updateUser = (userData) => {
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
    };

    const value = {
        user,
        loading,
        login,
        register,
        loginWithGoogle,
        logout,
        updateUser,
        isAuthenticated: !!user,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
